import { formatDate } from '@apparel/core/date-time';
import { MainTheme } from '@apparel/ui/theme/theme';
import { BulletText } from '@components/BulletItem';
import { PrimaryButton } from '@components/Buttons';
import { Color } from '@components/Color';
import { InlineLoading } from '@components/InlineLoading';
import { InlineBlock, InlineFlex } from '@components/Layout/Blocks';
import { Col } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { Link } from '@components/Link';
import { NothingToShow } from '@components/NothingToShow';
import { CATEGORY_TYPES } from '@containers/Notifications/List/constant';
import { useReduxState } from '@hooks/Redux/useReduxState';
import { useAuthRedux } from '@hooks/useAuthRedux';
import { useToggle } from '@hooks/useToggle';
import { useUser } from '@hooks/useUser';
import { getSubdomainData } from '@lib/utils';
import { useMediaQuery } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Divider from '@material-ui/core/Divider';
import Fade from '@material-ui/core/Fade';
import Grow from '@material-ui/core/Grow';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Notifications from '@material-ui/icons/Notifications';
import NotificationsOutlined from '@material-ui/icons/NotificationsOutlined';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { ApparelNotificationType } from '@model/ApparelNotification';
import { UserType } from '@model/User';
import { useApp } from '@packages/hooks/useApp';
import { Absolute, Fixed } from '@packages/layout/lib/Blocks';
import { THEMES } from '@packages/layout/lib/PageHeader/theme';
import { useLoading } from '@packages/loading';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import { BackdropBlur } from '@packages/ui/elements/BackdropBlur';
import { refreshNotifyAlerts } from '@redux/NotificationAlert/actions';
import { ApparelNotificationRepository } from '@repositories/ApparelNotificationRepository';
import { transparentize } from 'polished';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

export const NotificationBell = createMemo(
    () => {
        const [auth] = useAuthRedux();
        const user: UserType = useUser();

        const hasUser = !!(auth && user);
        const isCustomer = useMemo(() => user.user_view === 'customer', [user.user_view]);
        const dispatch = useDispatch();
        const id = 'notification-bell-dropdown';
        const anchor = useRef(null);
        const dialogRef = useRef(null);
        const toggle = useToggle(false);
        const classes = useStyles();
        const isMobile = useMediaQuery<MainTheme>((theme) => theme.breakpoints.down('xs'));
        const history = useHistory();
        const loader = useLoading(false);
        const repository = useApp(ApparelNotificationRepository);
        const wholesalerBySubdomain = getSubdomainData();

        const [notifications, setNotifications] = useState([]);
        const limit = 15;
        const [offset, setOffset] = useState(0);
        const [unreadNotifications, setUnreadNotifications] = useState(0);

        const hasItems = notifications.length > 0;
        const [isBusy, setIsBusy] = useState(false);

        const [hoveredNotification, setHoveredNotification] = useState(null);
        const state = useReduxState({
            unreadNotifyCount: 'NotificationAlert.unread_notification_count',
            notificationCount: 'NotificationAlert.notification_count',
        });
        const handleClose = useCallback(() => {
            toggle.setState(false);
        }, [toggle.setState]);

        const loadNotifications = async () => {
            await loader.start();

            const response = await repository.fetchApparelNotificationsForCurrentUser({
                limit: offset > 0 ? offset : limit,
                offset: 0,
            });

            const data = response.data.items;
            const unread = response.data.unread;

            if (data && data.length > 0) {
                setNotifications(data);
                await setOffset(offset + data.length);
            }
            if (state?.notificationCount === 0) {
                setNotifications([]);
            }

            setUnreadNotifications(unread);

            await loader.stop();
        };

        const loadMoreNotifications = async () => {
            await loader.start();

            const response = await repository.fetchApparelNotificationsForCurrentUser({
                limit: limit,
                offset: offset,
            });

            const data = response.data.items;
            const unread = response.data.unread;

            if (data && data.length > 0) {
                setNotifications(notifications.concat(data));
                await setOffset(offset + data.length);
            }

            setUnreadNotifications(unread);

            await loader.stop();
        };

        useEffect(() => {
            if (hasUser) {
                loadNotifications();
            }
        }, [hasUser, state?.unreadNotifyCount, state?.notificationCount]);

        const handleScroll = async (event) => {
            const node = event.target;
            const bottom = node.scrollHeight - node.scrollTop === node.clientHeight;

            if (bottom) {
                setIsBusy(true);
                await loadMoreNotifications();
                setIsBusy(false);
            }
        };

        const handleHide = (notification) => async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            await loader.start();
            setIsBusy(true);

            await repository.updateNotification(notification.id, { ...notification, read: true });
            await refreshNotifyAlerts(dispatch);
            setIsBusy(false);
        };
        const readAllNotifications = () => async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            await loader.start();
            setIsBusy(true);
            await repository.readAllNotifications();
            await refreshNotifyAlerts(dispatch);
            setIsBusy(false);
        };
        const handleClick = (notification) => async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            await loader.start();

            window.open(notification.link, '_blank');
            if (!notification.read) {
                notification.read = true;
                setUnreadNotifications(unreadNotifications - 1);
                await repository.updateNotification(notification.id, { ...notification, read: true });
                await refreshNotifyAlerts(dispatch);
            }
        };

        const handleMouseEnter = (notification) => (ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            setHoveredNotification(notification.id);
        };

        const handleMouseLeave = () => {
            setHoveredNotification(null);
        };

        const handleOpenMainView = useCallback(() => {
            history.push('/notifications/all');
        }, []);

        const content$ = useMemo(() => {
            if (!hasItems) {
                return (
                    <Row ref={dialogRef} padding={'0'}>
                        <Col xs={12} padding={'0'}>
                            <NothingToShow
                                title={'No notifications'}
                                icon={'notifications'}
                                text={"You don't have any notification."}
                                padding={30}
                            />
                        </Col>
                    </Row>
                );
            }

            return (
                <Row ref={dialogRef} padding={'0'}>
                    <Col xs={12} padding={'0'}>
                        <Row
                            padding={'8px 0'}
                            align={'center'}
                            className={isCustomer ? classes.greyBg : classes.dialogHeader}
                        >
                            <Col xs align={'center'} justify={'start'}>
                                <Typography display={'inline'} variant={'body2'}>
                                    Notifications{' '}
                                    {!isCustomer && (
                                        <span className={classes.unreadCount}>
                                            {unreadNotifications > 0 && `(${unreadNotifications})`}
                                        </span>
                                    )}
                                </Typography>
                            </Col>
                            {isCustomer && (
                                <Col xs align={'center'} justify={'end'} padding={0}>
                                    <PrimaryButton
                                        variant={'text'}
                                        background={'none'}
                                        size={'small'}
                                        margin={0}
                                        padding={'0 5px'}
                                        className={classes.readAll}
                                        onClick={readAllNotifications()}
                                    >
                                        READ ALL
                                    </PrimaryButton>
                                </Col>
                            )}
                            <Col align={'center'} justify={'end'} padding={'0 12px 0 6px'} style={{ width: 'auto' }}>
                                <Link color={'primary'} to={'/notifications/all'} className={classes.viewAll}>
                                    {isCustomer ? 'SEE ALL' : 'VIEW ALL'}
                                </Link>
                            </Col>
                        </Row>
                        <Divider />
                        <List className={classes.list} onScroll={handleScroll} disablePadding>
                            {notifications.map((notification: ApparelNotificationType) => {
                                const category = CATEGORY_TYPES.find((c) => c.id === notification?.category);
                                const Icon = category?.icon ?? null;
                                return IF(
                                    !notification.hidden,
                                    <>
                                        {isCustomer ? (
                                            <CustomerViewListItem
                                                notification={notification}
                                                handleClick={handleClick}
                                                handleMouseEnter={handleMouseEnter}
                                                handleMouseLeave={handleMouseLeave}
                                                hoveredNotification={hoveredNotification}
                                                handleHide={handleHide}
                                            />
                                        ) : (
                                            <ListItem
                                                button
                                                divider
                                                style={{
                                                    backgroundColor: notification.read ? '#fff' : '#FFF2F4',
                                                }}
                                                key={notification.id}
                                                onClick={handleClick(notification)}
                                                onMouseEnter={handleMouseEnter(notification)}
                                                onMouseLeave={handleMouseLeave}
                                                disableGutters
                                            >
                                                <Row direction={'row'}>
                                                    <Col xs={12} sm={12} justify={'start'}>
                                                        <ListItemText
                                                            primary={
                                                                <InlineFlex
                                                                    width={'100%'}
                                                                    justify={'space-between'}
                                                                    align={'flex-start'}
                                                                >
                                                                    {category && (
                                                                        <Box
                                                                            display={'flex'}
                                                                            width={'40px'}
                                                                            minWidth={'40px'}
                                                                            height={'40px'}
                                                                            borderRadius={'100px'}
                                                                            marginRight={'8px'}
                                                                            style={{
                                                                                backgroundColor: category.bgColor,
                                                                            }}
                                                                        >
                                                                            <Icon className={classes.categoryIcon} />
                                                                        </Box>
                                                                    )}
                                                                    <Box width={'100%'} display={'block'}>
                                                                        <Typography
                                                                            variant={'subtitle2'}
                                                                            className={classes.notifyTitle}
                                                                            title={notification.message}
                                                                        >
                                                                            {notification.message}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant={'caption'}
                                                                            color={'textSecondary'}
                                                                            title={formatDate(
                                                                                'MMM DD [at] LT',
                                                                                notification.created_at,
                                                                            )}
                                                                            className={classes.dateStyle}
                                                                        >
                                                                            {formatDate(
                                                                                'MMM DD [at] LT',
                                                                                notification.created_at,
                                                                            )}
                                                                        </Typography>
                                                                    </Box>
                                                                    {!notification.read && (
                                                                        <BulletText color={'#EA0A2A'} />
                                                                    )}
                                                                </InlineFlex>
                                                            }
                                                            disableTypography
                                                        />
                                                    </Col>
                                                    {isCustomer && (
                                                        <Col xs={1} align={'center'} justify={'end'} padding={0}>
                                                            {IF(
                                                                hoveredNotification &&
                                                                    notification.id === hoveredNotification &&
                                                                    !notification.read,
                                                                <IconButton
                                                                    onClick={handleHide(notification)}
                                                                    color={'inherit'}
                                                                >
                                                                    <Color
                                                                        component={VisibilityOff}
                                                                        color={'inherit'}
                                                                    />
                                                                </IconButton>,
                                                            )}
                                                        </Col>
                                                    )}
                                                </Row>
                                            </ListItem>
                                        )}
                                    </>,
                                );
                            })}
                            <Fade in={isBusy} unmountOnExit>
                                <BackdropBlur tintOpacity={0.7}>
                                    <Fixed b={0} l={0} r={0} width={'100%'} z={10}>
                                        <InlineLoading
                                            component={InlineFlex}
                                            width={'100%'}
                                            padding={14}
                                            align={'center'}
                                            justify={'center'}
                                        />
                                    </Fixed>
                                </BackdropBlur>
                            </Fade>
                        </List>
                    </Col>
                </Row>
            );
        }, [notifications, isBusy, offset, hoveredNotification, unreadNotifications]);

        return IF(
            hasUser,
            <>
                <IconButton
                    aria-describedby={id}
                    onClick={!isMobile || isCustomer ? toggle.handleToggle : handleOpenMainView}
                    ref={anchor}
                    color={'inherit'}
                    style={{ padding: '6px' }}
                >
                    <Badge
                        badgeContent={isCustomer ? unreadNotifications : state?.unreadNotifyCount}
                        color={'primary'}
                        style={{ zIndex: 3 }}
                    >
                        <Color
                            component={hasItems ? Notifications : NotificationsOutlined}
                            color={transparentize(
                                hasItems ? 0.2 : 0.5,
                                wholesalerBySubdomain?.header_config?.textColor || THEMES.dark.color,
                            )}
                        />
                    </Badge>
                    <Grow in={isBusy} unmountOnExit>
                        <Absolute component={Row} align={'center'} justify={'center'} t={0} l={0} r={0} b={0} z={1}>
                            <CircularProgress size={38} thickness={2} color={'primary'} />
                        </Absolute>
                    </Grow>
                </IconButton>
                <Popper
                    id={id}
                    open={toggle.state}
                    anchorEl={anchor.current as any}
                    placement={'bottom-end'}
                    disablePortal={false}
                    style={{ zIndex: 1210, maxWidth: 400, width: '100%', pointerEvents: toggle.state ? 'all' : 'none' }}
                    modifiers={{
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'viewport',
                        },
                    }}
                >
                    <InlineBlock
                        component={Paper}
                        elevation={4}
                        minWidth={300}
                        maxWidth={600}
                        w={'100%'}
                        backgroundColor={'rgba(255, 255, 255,.94)'}
                        borderRadius={isCustomer ? '4px' : '12px'}
                    >
                        <ClickAwayListener onClickAway={handleClose}>{content$}</ClickAwayListener>
                    </InlineBlock>
                </Popper>
            </>,
        );
    },
    {
        displayName: 'NotificationBell',
        defaultProps: {},
    },
);

const CustomerViewListItem = ({
    notification,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    hoveredNotification,
    handleHide,
}) => {
    return (
        <ListItem
            button
            divider
            style={{ backgroundColor: '#fff' }}
            key={notification.id}
            onClick={handleClick(notification)}
            onMouseEnter={handleMouseEnter(notification)}
            onMouseLeave={handleMouseLeave}
            disableGutters
        >
            <Row direction={'row'}>
                <Col xs={11} justify={'start'}>
                    <ListItemText
                        primary={
                            !notification.read ? (
                                <BulletText color={'info'}>
                                    <Typography
                                        variant={!notification.read ? 'body2' : 'body1'}
                                        noWrap
                                        title={notification.message}
                                    >
                                        {notification.message}
                                    </Typography>
                                </BulletText>
                            ) : (
                                <Typography
                                    variant={!notification.read ? 'body2' : 'body1'}
                                    noWrap
                                    title={notification.message}
                                >
                                    {notification.message}
                                </Typography>
                            )
                        }
                        secondary={
                            <Typography
                                variant={'body1'}
                                color={'textSecondary'}
                                title={formatDate('human', notification.created_at)}
                            >
                                {formatDate('human', notification.created_at)}
                            </Typography>
                        }
                        disableTypography
                    />
                </Col>
                <Col xs={1} align={'center'} justify={'end'} padding={0}>
                    {IF(
                        hoveredNotification && notification.id === hoveredNotification && !notification.read,
                        <IconButton onClick={handleHide(notification)} color={'inherit'}>
                            <Color component={VisibilityOff} color={'inherit'} />
                        </IconButton>,
                    )}
                </Col>
            </Row>
        </ListItem>
    );
};

const useStyles = makeStyles(() => ({
    categoryIcon: {
        width: '24px',
        height: '24px',
        color: '#fff',
        margin: 'auto',
        display: 'flex',
    },
    notifyTitle: {
        lineHeight: '20px',
        color: 'rgba(0, 0, 0, 0.87)',
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 2,
        'text-overflow': 'ellipsis',
    },
    viewAll: {
        fontSize: '14px',
        fontWeight: 500,
    },
    dialogHeader: {
        backgroundColor: 'rgb(255, 255, 255)',
        borderBottom: '1px solid rgb(224, 224, 224)',
        height: '57px',
        padding: '8px 0px',
        borderTopRightRadius: '12px',
        borderTopLeftRadius: '12px',
    },
    list: {
        maxHeight: '358px',
        overflow: 'auto',
        width: '100%',
        borderRadius: '12px',
    },
    dateStyle: {
        top: '3px',
        position: 'relative',
    },
    greyBg: {
        backgroundColor: '#eee',
    },
    readAll: {
        color: '#2193F3',
        cursor: 'pointer',
        fontWeight: 500,
        background: 'none !important',
        height: '24px !important',
        borderRadius: '0px !important',
        borderRight: '2px solid !important',
        borderRightColor: 'rgba(0, 0, 0, 0.24) !important',
    },
    unreadCount: {
        fontSize: '16px',
        fontWeight: 400,
        color: 'rgba(0,0,0,0.54)',
    },
}));
